import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withTheme } from 'styled-components';
import { withRouter } from 'react-router-dom';
import { Highcharts, Box, Text, Flex, Icon, Select, ButtonGroupTabs, ButtonGroupTab } from 'core/components';
import { MdOpenInNew } from 'react-icons/md';
import { addCommas } from 'app/util/utils';
import DataResolutionIndicator from './DataResolutionIndicator';

@inject('$colors')
@withTheme
@withRouter
@observer
export default class UtilizationChart extends Component {
  chartCallback = (chart) => {
    this.chart = chart.container ? chart : null;
  };

  componentDidUpdate(prevProps) {
    // Force chart update when metric changes to clear visual artifacts
    const { selectedMetric } = this.props;
    if (prevProps.selectedMetric !== selectedMetric) {
      // Force chart to redraw with new data when switching away from selected mode
      if (this.chart && selectedMetric !== 'selected') {
        setTimeout(() => {
          if (this.chart) {
            this.chart.redraw(true); // Force animation
          }
        }, 50);
      }
    }
  }

  findPointsByIndex = (clickedIndex) => {
    const { rawFlowArray, receivedFlowArray, storedFlowArray } = this.props;

    if (
      !rawFlowArray ||
      !receivedFlowArray ||
      !storedFlowArray ||
      clickedIndex < 0 ||
      clickedIndex >= rawFlowArray.length ||
      clickedIndex >= receivedFlowArray.length ||
      clickedIndex >= storedFlowArray.length
    ) {
      console.error('❌ Invalid index or arrays');
      return null;
    }

    const rawPoint = rawFlowArray[clickedIndex];
    const receivedPoint = receivedFlowArray[clickedIndex];
    const storedPoint = storedFlowArray[clickedIndex];

    if (
      Array.isArray(rawPoint) &&
      Array.isArray(receivedPoint) &&
      Array.isArray(storedPoint) &&
      rawPoint.length >= 2 &&
      receivedPoint.length >= 2 &&
      storedPoint.length >= 2
    ) {
      const result = {
        selectedTimestamp: receivedPoint[0], // Use received timestamp as reference
        selectedRawFlowValue: rawPoint[1],
        selectedReceivedFlowValue: receivedPoint[1],
        selectedStoredFlowValue: storedPoint[1],
        actualTimestamp: receivedPoint[0]
      };

      return result;
    }

    console.error('❌ Invalid point data at index:', clickedIndex);
    return null;
  };

  handleGoToPublicClouds = () => {
    const { history } = this.props;
    history.push('/v4/settings/clouds');
  };

  // Time period options
  timePeriodOptions = [
    { value: '1_day', label: '1 Day' },
    { value: '7_days', label: '7 Days' },
    { value: '30_days', label: '30 Days' }
  ];

  // Metric selection options (reordered with Peak first)
  metricOptions = [
    { value: 'peak', label: 'Peak' },
    { value: 'latest', label: 'Latest' },
    { value: 'selected', label: 'Selected' }
  ];

  handleMetricSelection = (metricValue) => {
    const { onMetricSelection } = this.props;
    // Call parent's metric selection handler
    if (onMetricSelection) {
      onMetricSelection(metricValue);
    }
  };

  handleTimePeriodChange = (selectedValue) => {
    const { onTimePeriodChange } = this.props;
    if (onTimePeriodChange && selectedValue) {
      // Convert value back to option object for compatibility with parent
      const selectedOption = this.timePeriodOptions.find((option) => option.value === selectedValue);
      if (selectedOption) {
        onTimePeriodChange(selectedOption);
      }
    }
  };

  get chartConfig() {
    const {
      receivedFlowArray,
      storedFlowArray,
      receivedFlowValue,
      storedFlowValue,
      selectedMetric,
      selectedPointData,
      pak_fps,
      onSelectedPointChange,
      theme
    } = this.props;
    // Calculate dynamic max value for the chart
    let dynamicMax = null;
    if (pak_fps) {
      // Find the maximum value in the data
      const maxDataValue = Math.max(
        ...(receivedFlowArray || []).map((point) => point[1] || 0),
        ...(storedFlowArray || []).map((point) => point[1] || 0),
        pak_fps
      );

      // Set chart max to accommodate data and zones
      if (maxDataValue > pak_fps * 2) {
        // If data exceeds 2x max, show slightly above the data
        dynamicMax = Math.ceil(maxDataValue * 1.1);
      } else if (maxDataValue > pak_fps) {
        // If data is between max and 2x max, show at least up to 2x max
        dynamicMax = pak_fps * 2.2;
      } else {
        // If data is below max, show a bit above max line for context
        dynamicMax = pak_fps * 1.3;
      }
    }
    /**
     * HIGHCHARTS CONFIGURATION DOCUMENTATION
     * =====================================
     *
     * This chart displays flow utilization data with colored zones to indicate traffic levels.
     * Key features and behaviors:
     *
     * COLORED ZONES (plotBands):
     * - Yellow zone: Traffic between pak_fps and pak_fps * 2 (above max line)
     * - Light red zone: Traffic above pak_fps * 2 (critical threshold)
     * - Uses semi-transparent colors (rgba with 0.3 opacity) for better visibility
     *
     * Y-AXIS SCALING:
     * - dynamicMax calculation prevents chart from being too cramped or too sparse
     * - endOnTick: false prevents Highcharts from extending axis beyond our calculated max
     * - tickInterval ensures readable tick spacing but can cause Highcharts to auto-scale
     * - Without endOnTick: false, charts may show different max values than calculated
     *
     * PLOT LINES:
     * - Red line at pak_fps value shows the maximum threshold
     * - zIndex: 5 ensures the line appears above plot bands and data
     *
     * INTERACTIVE FEATURES:
     * - Point clicking in "selected" mode highlights specific data points
     * - Hover states show detailed tooltips with timestamp and values
     * - Cursor changes to pointer in selected mode to indicate interactivity
     *
     * DATA SERIES:
     * - Received flows (blue): Primary traffic metric
     * - Stored flows (green): Processed/stored traffic metric
     * - Max line (red): Reference threshold line (legend only)
     * - Raw flows: Available in data but not displayed to reduce visual clutter
     *
     * COMMON ISSUES:
     * - If max values appear different than expected, check endOnTick and tickInterval
     * - Colored zones not appearing: Verify pak_fps prop is provided
     * - Interactive clicking not working: Ensure selectedMetric === 'selected'
     */

    // Create series data with selected point highlighting
    const createSeriesData = (data, selectedValue, selectedTimestamp) => {
      if (!data || !Array.isArray(data)) {
        return [];
      }

      // Only show highlights in selected mode with valid selected data
      if (selectedMetric === 'selected' && selectedTimestamp && selectedPointData) {
        return data.map((point) => {
          // Ensure point is an array [timestamp, value]
          if (!Array.isArray(point) || point.length < 2) {
            return point;
          }

          const [timestamp, value] = point;
          // Highlight the selected point using exact timestamp match
          if (timestamp === selectedTimestamp) {
            return {
              x: timestamp,
              y: value,
              marker: {
                enabled: true,
                radius: 6,
                fillColor: '#ff6b6b',
                lineColor: '#ffffff',
                lineWidth: 2
              }
            };
          }
          return [timestamp, value]; // Keep as array format
        });
      }

      // For all other cases (peak, latest, or selected with no data), return clean data
      // Ensure we return fresh arrays to force Highcharts to redraw
      return data.map((point) => {
        if (!Array.isArray(point) || point.length < 2) {
          return point;
        }
        // Return clean array data without any marker properties
        return [point[0], point[1]];
      });
    };

    const selectedTimestamp = selectedPointData?.actualTimestamp;

    return {
      colors: ['#137cbd', '#0f9960', '#ff0000'], // Explicit color mapping: blue, green, red
      chart: {
        type: 'line',
        height: 160,
        backgroundColor: 'transparent',
        spacing: [10, 0, 15, 0],
        style: {
          fontFamily: 'inherit'
        }
      },
      title: {
        text: null
      },
      credits: {
        enabled: false
      },
      xAxis: {
        type: 'datetime',
        labels: {
          style: {
            fontSize: '11px'
          }
        },
        lineWidth: 1,
        tickWidth: 1
      },
      yAxis: {
        title: {
          text: null
        },
        labels: {
          formatter() {
            if (this.value >= 1000) {
              return `${this.value / 1000}k`;
            }
            return this.value;
          },
          style: {
            fontSize: '11px'
          }
        },
        gridLineWidth: 1,
        min: 0,
        max: dynamicMax,
        // Control Highcharts automatic axis adjustments to prevent unwanted scaling
        // tickInterval can cause Highcharts to extend the max beyond our dynamicMax
        // Using endOnTick: false prevents axis extension for "nicer" tick values
        tickInterval: pak_fps ? Math.max(pak_fps / 4, 500) : undefined, // Ensure reasonable tick spacing
        endOnTick: false, // Prevent Highcharts from extending axis for "nice" tick values
        startOnTick: true, // Keep nice start value (0)
        plotLines: pak_fps
          ? [
              {
                color: '#ff0000', // Bright red color for the Max line
                dashStyle: 'Solid',
                value: pak_fps,
                width: 2,
                zIndex: 5
              }
            ]
          : [],
        plotBands: pak_fps
          ? [
              {
                // Yellow zone: from max line to 2x max
                from: pak_fps,
                to: pak_fps * 2,
                color: 'rgba(255, 235, 59, 0.3)', // Light yellow with transparency
                zIndex: 1
              },
              {
                // Light red/pink zone: from 2x max to top of chart
                from: pak_fps * 2,
                to: dynamicMax || pak_fps * 3,
                color: 'rgba(255, 138, 128, 0.3)', // Light red/pink with transparency
                zIndex: 1
              }
            ]
          : []
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: {
          fontSize: '11px',
          color: theme?.colors?.body ?? '#333'
        },
        symbolHeight: 8,
        symbolWidth: 8,
        symbolRadius: 0
      },
      plotOptions: {
        line: {
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 3
              }
            }
          },
          lineWidth: 2,
          states: {
            hover: {
              lineWidth: 2
            }
          },
          cursor: selectedMetric === 'selected' ? 'pointer' : 'default',
          tooltip: {
            valueDecimals: 0
          }
        }
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderRadius: 3,
        shadow: false
        // Remove custom formatter to let Highcharts handle colors properly
      },
      series: [
        {
          name: 'Received',
          data: createSeriesData(receivedFlowArray, receivedFlowValue, selectedTimestamp),
          color: '#137cbd', // Blueprint primary blue
          zIndex: 3,
          point: {
            events: {
              click:
                selectedMetric === 'selected'
                  ? (event) => {
                      try {
                        const pointIndex = event.point.index;

                        // Use the point's index to get corresponding values from all arrays
                        const indexBasedData = this.findPointsByIndex(pointIndex);
                        if (indexBasedData && onSelectedPointChange) {
                          onSelectedPointChange(indexBasedData);
                        }
                      } catch (error) {
                        console.error('Error handling point click:', error);
                      }
                    }
                  : undefined
            }
          }
        },
        {
          name: 'Stored',
          data: createSeriesData(storedFlowArray, storedFlowValue, selectedTimestamp),
          color: '#0f9960', // Blueprint success green
          zIndex: 2,
          point: {
            events: {
              click:
                selectedMetric === 'selected'
                  ? (event) => {
                      try {
                        const pointIndex = event.point.index;

                        // Use the point's index to get corresponding values from all arrays
                        const indexBasedData = this.findPointsByIndex(pointIndex);
                        if (indexBasedData && onSelectedPointChange) {
                          onSelectedPointChange(indexBasedData);
                        }
                      } catch (error) {
                        console.error('Error handling point click:', error);
                      }
                    }
                  : undefined
            }
          }
        },
        ...(pak_fps
          ? [
              {
                name: 'Max',
                data: [], // No data points, just for legend
                color: '#ff0000', // Bright red color matching the plotLine
                lineWidth: 2,
                dashStyle: 'Solid',
                marker: {
                  enabled: false
                },
                enableMouseTracking: false, // Disable hover effects
                showInLegend: true,
                zIndex: 1
              }
            ]
          : [])
        // Raw flow series removed from chart display, but raw data is still available for calculations
      ]
    };
  }

  render() {
    const { hideGoToPublicCloudsButton } = this.props;
    const {
      avgConfiguredSamplingRate,
      dataResolution,
      loading,
      error,
      receivedFlowValue,
      rawFlowValue,
      storedFlowValue,
      planMaxFps,
      selectedTimePeriod,
      selectedMetric,
      selectedPointData,
      flowValueTimestamp
    } = this.props;
    // Calculate percentages for display
    const receivedPercentage = receivedFlowValue ? ((receivedFlowValue / planMaxFps) * 100).toFixed(2) : null;
    const storedPercentage = storedFlowValue ? ((storedFlowValue / planMaxFps) * 100).toFixed(2) : null;

    if (loading) {
      return (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Utilization{' '}
          </Text>
          <Text color="muted">Loading metrics data...</Text>
        </Box>
      );
    }

    if (error) {
      return (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Utilization
          </Text>
          <Box p={2} backgroundColor="#fff2f0" borderRadius="3px" border="1px solid #ffccc7">
            <Text color="danger">Error loading metrics: {error}</Text>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        {/* Utilization Section Header */}
        <Flex justifyContent="space-between" alignItems="center" mb={2}>
          <Text fontWeight="bold">Utilization</Text>
          <Flex alignItems="center">
            <Select
              options={this.timePeriodOptions}
              values={selectedTimePeriod || '1_day'}
              onChange={this.handleTimePeriodChange}
              placeholder="Select time period"
              width={120}
            />
            {!hideGoToPublicCloudsButton && (
              <Icon icon={MdOpenInNew} iconSize={12} color="primary" onClick={this.handleGoToPublicClouds} ml={2} />
            )}
          </Flex>
        </Flex>

        {/* Chart */}
        <Box mb={3} style={{ cursor: selectedMetric === 'selected' ? 'pointer' : 'default' }}>
          {selectedMetric === 'selected' && (
            <Text fontSize="11px" color="#666666" mb={1} fontStyle="italic">
              <span role="img" aria-label="tip">
                💡
              </span>{' '}
              Click anywhere on the chart to select a data point
            </Text>
          )}
          <Highcharts
            key={`chart-${selectedMetric}`}
            allowChartUpdate
            height={160}
            options={this.chartConfig}
            callback={this.chartCallback}
            styledMode={false}
          />
        </Box>

        {/* Stats Table Headers and Values */}
        <Flex mb={3}>
          <Box flex="1">
            {/* Metric Selection Button Group */}
            <ButtonGroupTabs omitTabsWrapper selectedTabId={selectedMetric} onChange={this.handleMetricSelection}>
              {this.metricOptions.map((option) => (
                <ButtonGroupTab key={option.value} id={option.value} text={option.label} />
              ))}
            </ButtonGroupTabs>
          </Box>
          <Box flex="3">{/* This space will contain the stats values - can be populated with actual data */}</Box>
        </Flex>

        {/* Raw FPS Data */}
        <Box mt={3} p={2} backgroundColor="#f7f9fa" borderRadius="3px">
          {selectedMetric === 'selected' && !rawFlowValue && (
            <Box mb={2} p={2} backgroundColor="#e8f4fd" borderRadius="3px" border="1px solid #4f9eff">
              <Text color="#1a73e8" fontSize="12px" fontWeight="500">
                Click on the chart above to select a specific data point and view its metrics.
                {selectedPointData && (
                  <Text mt={1} fontWeight="bold" color="#1a73e8">
                    Selected point: {new Date(selectedPointData.actualTimestamp).toUTCString()}
                  </Text>
                )}
              </Text>
            </Box>
          )}
          {flowValueTimestamp && (
            <Flex justifyContent="space-between" alignItems="center" mb={1}>
              <Text fontWeight="bold">Time:</Text>
              <Text>{new Date(flowValueTimestamp).toUTCString()}</Text>
            </Flex>
          )}
          <Flex justifyContent="space-between" alignItems="center" mb={1}>
            <Text fontWeight="bold">Raw FPS:</Text>
            <Text>{rawFlowValue ? addCommas(Math.round(rawFlowValue)) : 'N/A'} flow/s</Text>
          </Flex>

          <Flex justifyContent="space-between" alignItems="center" mb={1}>
            <Text fontWeight="bold">Avg Configured Sampling Rate:</Text>
            <Flex alignItems="center">
              <Text>{avgConfiguredSamplingRate || 'N/A'}</Text>
              <DataResolutionIndicator
                dataResolution={dataResolution}
                avgConfiguredSamplingRate={avgConfiguredSamplingRate}
                useConfiguredSamplingRate
              />
            </Flex>
          </Flex>

          <Flex justifyContent="space-between" alignItems="center" mb={1}>
            <Text fontWeight="bold">Received FPS:</Text>
            <Text>
              {receivedFlowValue ? `${addCommas(Math.round(receivedFlowValue))} flow/s` : 'N/A'}
              {receivedPercentage && (
                <Text color="muted" ml={1}>
                  {receivedPercentage}%
                </Text>
              )}
            </Text>
          </Flex>

          <Flex justifyContent="space-between" alignItems="center" mb={1}>
            <Text fontWeight="bold">Stored FPS:</Text>
            <Text>
              {storedFlowValue ? `${addCommas(Math.round(storedFlowValue))} flow/s` : 'N/A'}
              {storedPercentage && (
                <Text color="muted" ml={1}>
                  {storedPercentage}%
                </Text>
              )}
            </Text>
          </Flex>

          {dataResolution !== 'ok' && (
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontWeight="bold">
                Data Resolution <Icon icon="info-sign" iconSize={12} color="muted" />:
              </Text>
              <Flex alignItems="center">
                <DataResolutionIndicator dataResolution={dataResolution} useConfiguredSamplingRate={false} />
              </Flex>
            </Flex>
          )}
        </Box>
      </Box>
    );
  }
}
